import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Monitor, Users, Store, Gamepad2, Leaf } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const skills = [
  "IT Management",
  "Customer Service",
  "Team Leadership",
  "Technical Support",
  "Law Enforcement",
  "Entrepreneurship",
  "Community Advocacy",
  "Business Operations",
  "Training & Mentoring",
  "Retail Management",
  "Dispute Resolution",
  "Data Analysis",
];

const highlights = [
  { icon: Shield, label: "Law Enforcement", desc: "Former Deputy Sheriff" },
  { icon: Monitor, label: "Apple Tech", desc: "5 years at Apple Inc." },
  { icon: Users, label: "Advocacy", desc: "Community engagement" },
  { icon: Store, label: "4 Companies", desc: "Serial entrepreneur" },
];

export const AboutSection = ({ profile }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="about-section"
      aria-label="About Tommy Green"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16"
        >
          {/* Left: Narrative */}
          <div>
            <motion.div variants={reveal}>
              <span className="mono-label text-primary mb-3 block">About Me</span>
            </motion.div>
            <motion.h2
              variants={reveal}
              className="text-xl sm:text-2xl font-semibold mb-6"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              From Deputy Sheriff to Tech Leader to Entrepreneur
            </motion.h2>
            <motion.p
              variants={reveal}
              className="text-sm sm:text-base text-muted-foreground leading-[1.55] mb-6"
              data-testid="about-summary-text"
            >
              {profile.profile_summary}
            </motion.p>
            <motion.p
              variants={reveal}
              className="text-sm sm:text-base text-muted-foreground leading-[1.55] mb-8"
            >
              Currently serving as the Men and Boys Engagement Specialist at The Advocates in Hailey, Idaho, 
              while simultaneously running four businesses across gaming, agriculture, security, and food & beverage. 
              My career has taken me from patrol cars to Apple's support engineering team, from managing retail stores 
              to building companies from the ground up.
            </motion.p>

            {/* Skill Badges */}
            <motion.div variants={reveal} className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <Badge
                  key={skill}
                  variant="secondary"
                  className="bg-secondary text-secondary-foreground border border-border hover:border-primary/40 transition-colors duration-200 cursor-default"
                >
                  {skill}
                </Badge>
              ))}
            </motion.div>
          </div>

          {/* Right: Stats Cards */}
          <div className="grid grid-cols-2 gap-4">
            {highlights.map((item, i) => (
              <motion.div key={item.label} variants={reveal}>
                <Card className={`p-5 bg-card border-border h-full ${i % 2 === 0 ? 'glow-card' : 'glow-card glow-card-gold'}`}>
                  <item.icon className={`w-6 h-6 ${i % 2 === 0 ? 'text-primary' : 'text-accent'} mb-3`} />
                  <div className="font-semibold mb-1" style={{ fontFamily: 'var(--font-display)' }}>{item.label}</div>
                  <div className="text-sm text-muted-foreground">{item.desc}</div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
